import React, { useState, useRef, useCallback, Fragment, useMemo } from "react";
import {
  Button,
  Row,
  Col,
  NMPopup,
  Input,
  TextButton,
  UserCard,
  DataGridView,
  NMFormPopup,
} from "@neumetric/blueprint-lib/components";
import { useSelector, useDispatch } from "react-redux";
import {
  appAction,
  getActiveUsers,
  getDepartments,
  commonAction,
  getUserDesignations,
  getCountryCodes,
  getCurrentUser,
  getUserRolesForModuleByCurrentUser,
} from "@neumetric/blueprint-lib/store";
import { getCurrentRisk, riskActions, isRiskAccepted, isRiskArchived, getDepartmentResponders } from "../../store";

const GridColumns = [
  {
    dataIndex: "department",
    title: "Department",
    typeRepresentation: "TEXT",
    width: "20%",
    filterFlag: true,
    verticalAlign: "middle",
  },
  {
    dataIndex: "responder",
    title: "Responder",
    typeRepresentation: "TEXT",
    width: "60%",
    editableFlag: true,
    editablePolicy: "ALWAYS",
    inputProps: {
      searchableFlag: true,
      template: "USER",
      highlight: true,
      addUserFlag: true,
      placeholder: "search Responder...",
    },
  },
  {
    dataIndex: "action",
    title: "Action",
    typeRepresentation: "CUSTOM",
    width: "20%",
    classes: { cell: "h-100" },
  },
];

const NewUserFormFields = [
  {
    label: "Firstname",
    attribute: "firstname",
    required: true,
    placeholder: "First name",
    typeRepresentation: "TEXT",
    classes: { root: "w-351-px", label: "w-136-px" },
  },
  {
    label: "Lastname",
    attribute: "lastname",
    required: true,
    placeholder: "Last name",
    typeRepresentation: "TEXT",
    classes: { root: "w-300-px" },
  },
  {
    label: "Email",
    attribute: "email",
    required: true,
    type: "email",
    placeholder: "Email",
    typeRepresentation: "TEXT",
    classes: { root: "w-440-px", label: "w-136-px" },
  },
  {
    collapsable: false,
    className: "w-100",
    childrenFields: [
      {
        label: "Mobile",
        attribute: "country_code",
        required: false,
        placeholder: "Afghanistan +88",
        typeRepresentation: "DROPDOWN",
        classes: { root: "w-340-px mr-8", label: "w-136-px" },
      },
      {
        label: null,
        attribute: "phone",
        required: false,
        type: "text",
        placeholder: "8888888888",
        typeRepresentation: "TEXT",
        classes: { root: "w-217-px" },
      },
    ],
  },
  {
    label: "Designation",
    attribute: "designation",
    required: true,
    placeholder: "Chief infomation security officer",
    typeRepresentation: "TEXT",
    classes: { root: "w-440-px", label: "w-136-px" },
  },
  {
    label: "Role",
    attribute: "role_id",
    required: true,
    placeholder: "Role",
    typeRepresentation: "DROPDOWN",
    classes: { root: "w-100", label: "w-136-px" },
  },
  {
    label: "Supervisor Email",
    attribute: "supervisor",
    required: true,
    placeholder: "Email",
    typeRepresentation: "TEXT",
    classes: { root: "w-440-px", label: "w-136-px" },
  },
  {
    label: "Department",
    attribute: "department_name",
    required: true,
    placeholder: "Administration",
    typeRepresentation: "DROPDOWN",
    classes: { root: "w-318-px mb-0", label: "w-136-px" },
  },
  {
    label: "Department Lead",
    attribute: "dept_lead",
    required: false,
    typeRepresentation: "CHECKBOX",
    classes: { root: "w-318-px mb-0" },
  },
];

const AddUserPopup = (props) => {
  const dispatch = useDispatch();
  const { department, onClose } = props;
  const _formRef = useRef(null);
  const currentUser = useSelector(getCurrentUser);
  const designations = useSelector(getUserDesignations);
  const countryCodes = useSelector(getCountryCodes);
  const departments = useSelector(getDepartments);
  const roles = useSelector(getUserRolesForModuleByCurrentUser.bind(null, "Console"));
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCancel = () => {
    onClose();
  };

  const handleFailure = (error) => {
    if (error.status === 422 && error.data && error.data.errors) {
      const errorMessage = Object.keys(error.data.errors)
        .map((key) => {
          if (key === "base") {
            return error.data.errors[key][0];
          }
          return `${key} ${error.data.errors[key][0]}`;
        })
        .join("<br />");
      dispatch(appAction.setAlert({ message: errorMessage, severity: "ERROR" }));
    }
    setIsSubmitting(false);
  };

  const handleConfirm = () => {
    setIsSubmitting(true);
    const values = _formRef.current.getValues();
    const payload = { ent_usr: { ...values, ent_org_id: currentUser.org.id }, department: department };
    const action = riskActions.updateRiskDeptResponderWithNewUser(payload);
    action.onComplete = () => {
      setIsSubmitting(false);
      onClose();
    };
    action.onFailure = handleFailure;
    dispatch(action);
  };

  const getOptions = (attribute) => {
    switch (attribute) {
      case "department_name":
        return departments;
      case "designation":
        return designations || [];
      case "country_code":
        return countryCodes || [];
      case "role_id":
        return roles || [];
      default:
        return [];
    }
  };

  return (
    <NMFormPopup
      ref={_formRef}
      width={701}
      variant="INFO"
      visible={true}
      title="Create New User"
      isSubmitting={isSubmitting}
      fields={NewUserFormFields}
      onClose={handleCancel}
      getOptions={getOptions}
      formData={{}}
      actions={[
        {
          label: "Cancel",
          icon: "CANCEL",
          btnType: "TEXT",
          disabled: isSubmitting,
          onClick: handleCancel,
        },
        {
          label: "Confirm",
          icon: "CHECK_CONFIRM",
          type: "submit",
          loading: isSubmitting,
          onClick: handleConfirm,
        },
      ]}
    />
  );
};

const AddResponderPopup = (props) => {
  const dispatch = useDispatch();
  const currentRisk = useSelector(getCurrentRisk);
  const users = useSelector(getActiveUsers);

  const filteredUsers = useMemo(() => {
    return users.filter((user) => !currentRisk.risk_responders.some((responder) => responder.id === user.id));
  }, [users, currentRisk.risk_responders]);

  const [state, setState] = useState({
    selectedResponder: null,
    submitting: false,
  });

  const updateState = (newState) => {
    setState({ ...state, ...newState });
  };

  const handleConfirm = () => {
    updateState({ submitting: true });
    const newResponder = users.find((user) => user.id === state.selectedResponder);
    const comment = { comment: `${newResponder.name} added as a Responder.` };
    const action = riskActions.addRiskResponder({ responderId: state.selectedResponder, comment });
    action.onComplete = () => {
      handleClose(null, true);
    };
    action.onFailure = () => {
      updateState({ submitting: false });
    };
    dispatch(action);
  };

  const handleClose = (_e, scroll = false) => {
    updateState({ submitting: false });
    props.onClose(scroll);
  };

  return (
    <NMPopup
      width={472}
      visible={true}
      variant="INFO"
      title="Add Responder"
      closeOnEscape={false}
      classes={{ title: "w-100", header: "pb-12", footer: "mt-0" }}
      actions={[
        {
          btnType: "OUTLINED",
          variant: "ACTION",
          label: "Cancel",
          icon: "CANCEL",
          disabled: state.submitting,
          onClick: handleClose,
        },
        {
          btnType: "CONTAINED",
          variant: "ACTION",
          label: "Add Responder",
          icon: "ADD",
          disabled: state.selectedResponder === null,
          loading: state.submitting,
          onClick: handleConfirm,
        },
      ]}
    >
      <Col>
        This Responder you will choose will have access to the risk.
        <br />
        Search and Select the responder below.
        <Input
          typeRepresentation="DROPDOWN"
          searchableFlag={true}
          classes={{ root: "w-100 mt-16 mb-0" }}
          options={filteredUsers}
          value={state.selectedResponder}
          multiSelection={false}
          label="Responder"
          labelAlign="middle"
          onChange={(userId) => updateState({ selectedResponder: userId })}
          placeholder="search Responder..."
          template="USER"
          labelTemplate="LIST"
        />
      </Col>
    </NMPopup>
  );
};

export const RemoveResponderPopup = (props) => {
  const currentRisk = useSelector(getCurrentRisk);
  const isRiskAccept = useSelector(isRiskAccepted);
  const isRiskArchive = useSelector(isRiskArchived);
  const dispatch = useDispatch();
  const { responder, onClose, allowRemove = true } = props;
  console.log("responder", responder);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRemove = () => {
    const comment = { comment: `${responder.ent_usr.name} removed as a Responder.` };
    const action = riskActions.removeRiskResponder({ responderId: responder.id, comment });
    action.onComplete = onClose;
    action.onFailure = () => {
      setIsSubmitting(false);
    };
    dispatch(action);
  };

  const getActions = () => {
    if (isRiskAccept || isRiskArchive || currentRisk.risk_responders.length === 1 || !allowRemove) {
      return [];
    }
    return [
      {
        buttonType: "CONTAINED",
        variant: "CAUTION",
        label: "Remove",
        icon: "DELETE",
        loading: isSubmitting,
        onClick: handleRemove,
      },
    ];
  };

  const actions = getActions();

  return (
    <NMPopup
      width={360}
      visible={true}
      variant="INFO"
      closeOnEscape={true}
      onModalClose={onClose}
      classes={{
        header: "pb-0 d-hide",
        footer: "",
        content: actions.length === 0 ? "pb-16" : "",
      }}
      actions={actions}
    >
      <UserCard className="w-100 mt-16" user={responder.ent_usr} showClose onClose={onClose} />
    </NMPopup>
  );
};

export const RiskResponder = () => {
  const isRiskAccept = useSelector(isRiskAccepted);
  const isRiskArchive = useSelector(isRiskArchived);
  const containerRef = useRef(null);
  const currentRisk = useSelector(getCurrentRisk);
  const [showAddResponderPopup, setShowAddResponderPopup] = useState(false);
  const [responderToDelete, setResponderToDelete] = useState(null);

  const handleAddResponder = () => {
    setShowAddResponderPopup(true);
  };

  const handleRemoveResponder = useCallback((responder) => {
    setResponderToDelete(responder);
  }, []);

  const handleClose = (scroll = false) => {
    setShowAddResponderPopup(false);
    setResponderToDelete(null);
    const el = containerRef.current;
    if (el && scroll) {
      el.scrollTop = el.scrollHeight - el.clientHeight + 32;
    }
  };

  return (
    <React.Fragment>
      <Row className="w-40 mt-2 overflow-hide" align="start">
        <Col className="w-102-px mt-4">
          <span className="ff-exo2 line-22 mr-5 text-filled fs-16 fw-500">Responders:</span>
          {!isRiskAccept && !isRiskArchive && (
            <Button label="Add" icon="ADD" classes={{ button: "mt-7" }} onClick={handleAddResponder} />
          )}
        </Col>
        <Col className="w-50 h-192-px overflow-auto" ref={containerRef}>
          {Array.isArray(currentRisk.risk_responders) &&
            currentRisk.risk_responders.map((responder) => (
              <TextButton
                key={responder.id}
                classes={{ label: "fw-400", button: "flex-shrink-0" }}
                label={responder.label}
                onClick={() => handleRemoveResponder(responder)}
              />
            ))}
        </Col>
      </Row>
      {showAddResponderPopup && <AddResponderPopup onClose={handleClose} />}
      {responderToDelete && <RemoveResponderPopup onClose={handleClose} responder={responderToDelete} />}
    </React.Fragment>
  );
};

export const RiskResponderGrid = () => {
  const _ref = useRef({});
  const dispatch = useDispatch();
  const departments = useSelector(getDepartments);
  const deptResponders = useSelector(getDepartmentResponders);
  const users = useSelector(getActiveUsers);
  const [showAddUserPopup, setShowAddUserPopup] = useState("");

  // const getDeptTables = () => {
  //   const leftTable = departments.slice(0, 8);
  //   const rightTable = departments.slice(8);
  //   return [leftTable, rightTable];
  // };
  const getDeptTables = () => {
    const filteredDepartments = departments.filter(
      (department) => department.id?.toLowerCase() !== "everyone"
    );

    const leftTable = filteredDepartments.slice(0, 8);
    const rightTable = filteredDepartments.slice(8);

    return [leftTable, rightTable];
  };

  const getDeptTableRows = useCallback(
    (iDepartments) => {
      const data = iDepartments.map((department) => {
        const deptResponder = deptResponders.find(
          (deptResponder) => deptResponder.department_name.toLowerCase() === department.id.toLowerCase()
        );
        return {
          department: department.label,
          responder: deptResponder && deptResponder.responder ? deptResponder.responder.id : "",
        };
      });
      return data;
    },
    [deptResponders]
  );
  console.log("deptResponders", deptResponders);
  console.log("departments", departments);
  console.log("users", users);

  const getCellSemantics = useCallback((column, record) => {
    const sematics = {};
    if (column.dataIndex === "responder") {
      sematics.possibleValues = users;
    }
    return sematics;
  }, []);

  const handleAddUser = (record) => {
    dispatch(commonAction.fetchCountryCodes());
    dispatch(commonAction.fetchUserDesignations());
    dispatch(commonAction.fetchUserRoles());
    setShowAddUserPopup(record.department);
  };

  const handleAssign = (record) => {
    const { department, responder } = record;
    if (department && _ref.current[department] && _ref.current[department] !== responder) {
      const action = riskActions.updateRiskDeptResponder({
        departmentId: department,
        userId: _ref.current[department],
      });
      dispatch(action);
    }
  };

  const handleSetCellValue = (cellInfo, value) => {
    const department = cellInfo.record.department;
    if (department) {
      _ref.current[department] = value;
    }
  };

  const ActionCellRenderer = (record) => {
    const { responder } = record;
    return (
      <Button
        label={responder ? "Change" : "Assign"}
        icon={responder ? "TRANSFER" : "ASSIGN"}
        onClick={() => handleAssign(record)}
      />
    );
  };

  const handleClose = (userId) => {
    setShowAddUserPopup("");
  };

  const getColumns = useCallback(() => {
    return GridColumns.map((column) => {
      if (column.dataIndex === "responder") {
        return { ...column, inputProps: { ...column.inputProps, onAddUser: handleAddUser } };
      } else if (column.dataIndex === "action") {
        return { ...column, cellRender: ActionCellRenderer };
      }
      return column;
    });
  }, []);

  return (
    <Fragment>
      <Row className="w-100 responders-grid-container gap-50" justify="center" align="start">
        {getDeptTables().map((departments, index) => {
          return (
            <Col className="w-39 h-500-px" key={index}>
              <DataGridView
                columns={getColumns()}
                dataSource={getDeptTableRows(departments)}
                getCellSemantics={getCellSemantics}
                setCellValue={handleSetCellValue}
              />
            </Col>
          );
        })}
      </Row>
      {showAddUserPopup && <AddUserPopup department={showAddUserPopup} onClose={handleClose} />}
    </Fragment>
  );
};
